import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowForward } from '@mui/icons-material';
import { Box, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import GuardedLink from '../../../../common/auth/GuardedLink';
import { FormAutocomplete, FormCheckbox, FormTextField } from '../../../../common/form';
import { ResourceType, UpdateExternalPricelistDto } from '../../../../common/PricePrgClient';
import { ProgressButton } from '../../../../common/ProgressButton';
import { RootState } from '../../../../store';
import { transformOptionalNumber } from '../../../../utils';
import { getPricelists } from '../../../pricelist/redux/pricelistSelectors';
import { pricelistsSelected } from '../../../pricelist/redux/pricelistSlices';
import { showConfirmDialog } from '../../../ui/redux/uiSlices';
import {
    getSelectedExternalPricelist,
    getSelectedExternalPricelistPermission
} from '../../redux/externalPricelistSelectors';
import { updateExternalPricelist } from '../../redux/externalPricelistSlices';

const mapStateToProps = (state: RootState) => ({
    selectedExternalPricelist: getSelectedExternalPricelist(state),
    pricelists: getPricelists(state),
    canWrite: getSelectedExternalPricelistPermission(state)?.canUpdate ?? false
});

type Props = ReturnType<typeof mapStateToProps>;

const schema: yup.SchemaOf<UpdateExternalPricelistDto> = yup.object().shape({
    name: yup.string().required().max(40).label('Name'),
    includeVat: yup.boolean().required().label('Include VAT'),
    marginThreshold: yup.number().required().min(0).max(100).label('Margin Threshold'),
    externalPricelistId: yup.number().required(),
    inPricelistId: yup.number().optional().transform(transformOptionalNumber)
});

const GeneralExternalPricelistTab = ({ selectedExternalPricelist, pricelists, canWrite }: Props) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, dirtyFields }
    } = useForm<UpdateExternalPricelistDto>({
        resolver: yupResolver(schema),
        defaultValues: useMemo(
            () => ({
                includeVat: selectedExternalPricelist?.includeVat,
                marginThreshold: selectedExternalPricelist?.marginThreshold,
                name: selectedExternalPricelist?.name,
                inPricelistId: selectedExternalPricelist?.pricelistId
            }),
            [selectedExternalPricelist]
        )
    });
    const dispatch = useDispatch();
    const pricelistIds = useMemo(() => Object.values(pricelists).map(c => c.pricelistId), [pricelists]);

    const [externalPricelist, setExternalPricelist] = useState({
        name: selectedExternalPricelist?.name ?? '',
        externalPricelistRef: selectedExternalPricelist?.externalPricelistRef ?? ''
    });

    useEffect(() => {
        reset({
            externalPricelistId: selectedExternalPricelist?.externalPricelistId ?? 0,
            includeVat: selectedExternalPricelist?.includeVat ?? false,
            inPricelistId: selectedExternalPricelist?.pricelistId,
            marginThreshold: selectedExternalPricelist?.marginThreshold ?? 0,
            name: selectedExternalPricelist?.name ?? ''
        });
        setExternalPricelist({
            name: selectedExternalPricelist?.name ?? '',
            externalPricelistRef: selectedExternalPricelist?.externalPricelistRef ?? ''
        });
    }, [selectedExternalPricelist, reset, setExternalPricelist]);

    if (selectedExternalPricelist == null) {
        return (
            <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 400, marginTop: '5%' }}>
                Choose a pricelist to view more details about it.
            </Typography>
        );
    }

    const submit = (form: UpdateExternalPricelistDto) => {
        if (dirtyFields.inPricelistId) {
            dispatch(
                showConfirmDialog({
                    dialogTitle: 'External prices will be removed',
                    dialogMessage:
                        'By changing pricelist, the current external prices will be removed. Are you sure you want to continue?',
                    primaryActionName: 'Change pricelist',
                    okAction: updateExternalPricelist.request(form)
                })
            );
        } else {
            dispatch(updateExternalPricelist.request(form));
        }
    };

    const dispatchGotoSelectedPricelist = (pricelistId: number) => {
        dispatch(pricelistsSelected([pricelistId]));
    };

    return (
        <Grid container sx={{ paddingLeft: '15px' }}>
            <Grid item xs={12} md={12}>
                <Typography component="h1" variant="h5">
                    {externalPricelist.externalPricelistRef} - {externalPricelist.name}
                </Typography>
                {/* TODO: Check if Box can be used instead of form here*/}
                <Box component="form" id="externalPricelistDetailsForm" onSubmit={handleSubmit(submit)}>
                    <Grid container direction="row" spacing={2}>
                        <Grid item sx={{ py: 2 }} xs={12} md={12} lg={3}>
                            <TextField
                                disabled={true}
                                label="Reference"
                                value={externalPricelist.externalPricelistRef || ''}
                                variant="standard"
                            />
                        </Grid>
                        <Grid item sx={{ py: 2 }} xs={12} md={12} lg={3}>
                            <FormTextField
                                label="Name"
                                control={control}
                                name="name"
                                fullWidth
                                variant="standard"
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="row" spacing={6}>
                        <Grid item sx={{ py: 2 }} xs={12} md={12} lg={3}>
                            <FormTextField
                                control={control}
                                label="Margin Threshold (%)"
                                type="number"
                                name="marginThreshold"
                                variant="standard"
                            />
                        </Grid>
                        <Grid item sx={{ py: 2 }} xs={12} md={12} lg={3}>
                            <FormAutocomplete
                                control={control}
                                name="inPricelistId"
                                label="Pricelist"
                                getOptionLabel={pricelist =>
                                    pricelists[pricelist] != null
                                        ? `${pricelists[pricelist!]?.pricelistRef} - ${
                                              pricelists[pricelist!].name
                                          }`
                                        : ''
                                }
                                id="pricelist"
                                options={pricelistIds}
                                clearOnEscape
                                sx={{ width: '300px', display: 'inline-flex' }}
                                disabled={!canWrite}
                                renderInput={() =>
                                    selectedExternalPricelist.pricelistId != null && (
                                        <GuardedLink
                                            resource={ResourceType.Pricelist}
                                            link={'/pricelists'}
                                            childComponents={
                                                <Tooltip
                                                    sx={{ display: 'inline-flex' }}
                                                    title="Go to pricelist"
                                                >
                                                    <IconButton
                                                        onClick={() =>
                                                            dispatchGotoSelectedPricelist(
                                                                selectedExternalPricelist.pricelistId!
                                                            )
                                                        }
                                                    >
                                                        <ArrowForward />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        />
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="row" spacing={2}>
                        <Grid item sx={{ py: 2 }} xs={12}>
                            <FormCheckbox
                                control={control}
                                name="includeVat"
                                label="Include VAT in margin calculations"
                            />
                        </Grid>
                        <Grid item>
                            <ProgressButton
                                variant="contained"
                                color="primary"
                                type="submit"
                                form="externalPricelistDetailsForm"
                                isLoading={false}
                                disabled={!isDirty}
                            >
                                Save
                            </ProgressButton>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(GeneralExternalPricelistTab);
